import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { InputField } from '@components/Input';
import { SaaeLogo } from '@components/SaaeLogo';
import { AuthSplitLayout } from '@layouts/AuthSplitLayout';
import { registerFormSchema, type RegisterFormValues } from '@schemas/formSchemas';
import { authRegister, API_ERROR_CLIENT_PHONE_NOT_ON_DEAL, type AuthOtpContext } from '@services/api';
import type { ApiError } from '@services/api';
import saFlagSrc from '../public/images/saFlag.svg';
import { Button } from '@components/Button/Button';

const COUNTRY_CODE = '+966';

export interface RegisterPageProps {
  onNext?: (phone: string) => void;
  /** When false, only the left panel is rendered (hero is static in parent). */
  showRightPanel?: boolean;
  /** When true, render only the center content (no layout/header/footer); used inside AuthFlowShell. */
  contentOnly?: boolean;
  /**
   * broker: platform login (no pending-deal check).
   * client: payer flow — phone must be on a Pending deal.
   */
  authContext?: AuthOtpContext;
  /** Payer flow: deal context for strict phone validation */
  clientDealId?: string;
  /** Payer flow: payment token from link query */
  clientToken?: string;
}

export function RegisterPage({
  onNext,
  showRightPanel = true,
  contentOnly = false,
  authContext = 'broker',
  clientDealId,
  clientToken,
}: RegisterPageProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: { mobile: '' },
    mode: 'onChange',
  });

  const mobileDirty = dirtyFields.mobile;
  const mobileError = errors.mobile?.message;
  const mobileValid = mobileDirty && !mobileError;

  const onSubmit = async (data: RegisterFormValues) => {
    const digits = (data.mobile ?? '').replace(/\D/g, '');
    const normalized = digits.startsWith('966') ? digits.slice(3) : digits.startsWith('0') ? digits.slice(1) : digits;
    const fullPhone = normalized ? `${COUNTRY_CODE}${normalized}` : COUNTRY_CODE;
    setApiError(null);
    setLoading(true);
    try {
      await authRegister(
        fullPhone,
        authContext === 'client' ? { authContext: 'client', dealId: clientDealId, token: clientToken } : undefined,
      );
      onNext?.(fullPhone);
    } catch (err) {
      const e = err as ApiError;
      if (authContext === 'client' && e.code === API_ERROR_CLIENT_PHONE_NOT_ON_DEAL) {
        setApiError(t('payer.errors.phoneNotOnDeal'));
      } else {
        setApiError(e.message ?? t('auth.register.errorSendOtp', { defaultValue: 'فشل إرسال الرمز. حاول مرة أخرى.' }));
      }
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className="w-full max-w-md flex flex-col items-center gap-4 max-[767px]:w-[calc(100%-3rem)] max-[767px]:max-w-md">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex flex-col items-center gap-4">
          <SaaeLogo size="large" mode="light" className="!h-[39px] w-auto" />
          <h1 className="font-sans text-2xl font-semibold text-primary m-0 max-w-md max-[767px]:text-xl leading-tight">
            {t('auth.register.headline')}
          </h1>
        </div>
        <p className="font-sans text-base font-normal text-gray-600 m-0 pt-4 max-w-sm leading-relaxed">
          {t('auth.register.subtext')}
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col items-center gap-4" noValidate>
        <div className="flex flex-col gap-2 items-start w-full [&_.max-w-xs]:max-w-none">
          <Controller
            control={control}
            name="mobile"
            render={({ field }) => (
              <InputField
                className="force-ltr"
                contentDir="ltr"
                size="lg"
                label={t('auth.register.mobileNumber')}
                type="tel"
                placeholder={t('auth.register.placeholder')}
                value={field.value}
                onChange={(e) => field.onChange((e.target as HTMLInputElement).value.replace(/\D/g, ''))}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                state={mobileError ? 'error' : mobileValid && field.value.length > 0 ? 'success' : 'default'}
                helperText={mobileError ? t(mobileError) : undefined}
                showHelperIcon={true}
                leftAddon={
                  <span className="inline-flex items-center gap-1 text-base font-normal text-gray-900">
                    <img src={saFlagSrc} alt="" width={21} height={15} className="shrink-0 object-contain" aria-hidden />
                    <span>+966</span>
                  </span>
                }
                fullWidth
              />
            )}
          />
        </div>
        {apiError && (
          <p className="text-sm text-surface-error-primary font-sans" role="alert">{apiError}</p>
        )}
        <Button type="submit" variant="primary" size="lg" fullWidth className="h-14 max-w-md rounded-[16px]" disabled={loading}>
          {loading ? t('common.loading', { defaultValue: 'جاري الإرسال…' }) : t('auth.register.next')}
        </Button>
      </form>
    </div>
  );

  if (contentOnly) return content;

  return (
    <AuthSplitLayout showBack={false} showRightPanel={showRightPanel}>
      {content}
    </AuthSplitLayout>
  );
}
